import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import ShiftAndTimeService from "@/services/ShiftAndTime/ShiftAndTime"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ThrowError } from "@/api/callApi"
import { showToastNotification } from "@/utility/helper"
import type { IPageInfo } from "@/interface/common.interface"
import TablePagination from "@/components/common/TablePagination"
import type { TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import { RoutesLink } from "@/utility/RouteLinks"
import { Pencil, Plus, Trash, TriangleAlert, MoreHorizontal } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import CommonTable from "@/components/common/CommonTable"
import CommonModal from "@/components/common/CommonModal"
import type { shiftAndTime } from "@/interface/shiftAndTime.interface"

const ShiftAndTime: React.FC = () => {
    const navigate = useNavigate()
    const [shiftAndTime, setShiftAndTime] = useState<shiftAndTime[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };

    const handleGetShiftAndTime = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        setIsLoading(true);
        try {
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}`;
            const response = await ShiftAndTimeService?.getShiftAndTime(PATH);
            if (response?.status) {
                setShiftAndTime(response?.data?.shift || []);
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }));
            }
            console.log("response", response)
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false);
        }
    }


    const handleDeleteShiftAndTime = async () => {
        try {
            const response = await ShiftAndTimeService?.deleteShiftAndTime(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message)
                toggleDeleteDialog("")
                handleGetShiftAndTime();
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
    }
    useEffect(() => {
        handleGetShiftAndTime(paginationData.currentPage, paginationData.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize]);

    const toggleDeleteDialog = (id: string | undefined) => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen(!deleteDialogOpen);
    }
    const columns: TableColumn<shiftAndTime>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: shiftAndTime, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'departmentName',
            header: 'Department Name',
            render: (item: shiftAndTime) => (
                <span className="font-semibold capitalize">{item?.shiftName}</span>
            )
        },
        {
            key: 'startTime',
            header: 'Start Time',
            render: (item: shiftAndTime) => (
                <span className="font-semibold capitalize">{item?.startTime}</span>
            )
        },
        {
            key: 'endTime',
            header: 'End Time',
            render: (item: shiftAndTime) => (
                <span className="font-semibold capitalize">{item?.endTime}</span>
            )
        },
        {
            key: 'checkInBufferTime',
            header: 'Check-in Buffer',
            render: (item: shiftAndTime) => (
                <span className="font-semibold capitalize">{item?.checkInBufferTime}</span>
            )
        },
        {
            key: 'checkOutBufferTime',
            header: 'Check-out Buffer',
            render: (item: shiftAndTime) => (
                <span className="font-semibold capitalize">{item?.checkOutBufferTime}</span>
            )
        },
        {
            key: 'type',
            header: 'Type',
            render: (item: shiftAndTime) => (
                <span className="font-semibold capitalize">{item?.type}</span>
            )
        },
        // {
        //     key: 'status',
        //     header: 'Status',
        //     render: (item: shiftAndTime) => (
        //         <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isActive ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
        //             {item?.isActive ? 'Active' : 'Inactive'}
        //         </span>
        //     )
        // },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: shiftAndTime) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[170px] rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            onClick={() => navigate(`${RoutesLink.editShiftAndTime}/${item?.id}`)}
                            className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50"
                        >
                            <Pencil className="h-4 w-4" />Edit Shift
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => toggleDeleteDialog(item?.id)}
                            className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50 text-red-600 focus:text-red-600"
                        >
                            <Trash className="h-4 w-4" />Delete Shift
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ]
    return (
        <>
            <Layout>
                <div className="space-y-6">
                    <MainHeading title="Shift and Time" isBackButtonVisible={true} buttonText='Add Shift and Time'
                        buttonIcon={<Plus />}
                        onClick={() => { navigate(RoutesLink.addShiftAndTime) }} />
                     <CommonTable data={shiftAndTime} columns={columns} loading={isLoading} />
                     <TablePagination
                         paginationData={paginationData}
                         setPaginationData={setPaginationData}
                         handleChangePageSize={handleChangePageSize}
                         isLoading={isLoading}
                     />

                </div>
            </Layout>

            <CommonModal
                title=""
                show={deleteDialogOpen}
                onClose={() => toggleDeleteDialog("")}
                className="max-w-md rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <TriangleAlert className="text-red-600 w-8 h-8" />
                        </div>

                        <h2 className="mt-5 text-2xl font-bold text-gray-900">
                            Delete Department?
                        </h2>

                        <p className="mt-3 text-sm leading-6 text-gray-500">
                            This action cannot be undone. Once deleted, all department data will be
                            permanently removed from our servers.
                        </p>
                    </div>

                    <div className="mt-8 flex gap-3">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => toggleDeleteDialog("")}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={handleDeleteShiftAndTime}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </CommonModal>
        </>
    )
}

export default ShiftAndTime